import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { locationsIDs, Theme } from '@constants';
import {
  useContextReference,
  useGetLocationFromCoords,
  useGetLocationsSearch,
  useKeyboard,
  useNodeHandle,
} from '@hooks';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { FocusableFlatList } from '../../focusable/FocusableFlatList/FocusableFlatList';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { TWCTextInput } from '../../primitives/TWCTextInput/TWCTextInput';
import { segmentEventTypes } from '../../segment/segmentEventTypes';
import { segmentTrackEvent } from '../../segment/SegmentTrackEvent';

const tPath = 'locations.addLocationPanel';

const US_TEXT = ', United States';

const keyboardBackground = Theme.withOpacity(Theme.blackPearlBlue, 0.8);

const keyboardWidth = 740;

const SearchResultsItem = forwardRef(
  ({ item, index, length, onPress, testID, nextFocusUp, nextFocusDown, nextFocusLeft, nextFocusRight }, reference) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    return (
      <FocusableHighlight
        accessibilityHint={index === length - 1 ? '' : t('searchResultsItemButtonAccessibilityHint')}
        accessibilityLabel={t('searchResultsItemButtonAccessibilityLabel', {
          location: item.text,
          index: index + 1,
          length,
        })}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={styles.itemTextWrapper}
        styleFocused={styles.itemTextWrapperFocused}
        testID={testID}
        underlayColor={Theme.white}
        onPress={onPress}
      >
        <SearchResultsItemText item={item} />
      </FocusableHighlight>
    );
  },
);

SearchResultsItem.propTypes = {
  item: PropTypes.object.isRequired,
  onPress: PropTypes.func,
  testID: PropTypes.string,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

SearchResultsItem.defaultProps = {
  onPress: () => {},
  testID: null,
  nextFocusUp: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
};

SearchResultsItem.displayName = 'SearchResults';

const SearchResultsItemText = ({ item, ...properties }) => {
  const topLine = item.place_name.slice(0, item.place_name.indexOf(','));
  const bottomLine = item.place_name.slice(item.place_name.indexOf(',')).slice(2).replace(US_TEXT, '');

  return (
    <>
      <TWCText
        numberOfLines={1}
        // TODO: if value focused is boolean then remove double negation
        style={properties.focused ? [styles.itemText, styles.itemTextFocused] : styles.itemText}
        testID={`searchResultText${item.id}`}
      >
        {topLine}
      </TWCText>
      <TWCText
        fontFamily={'light'}
        numberOfLines={1}
        style={properties.focused ? [styles.itemTextSmall, styles.itemTextFocused] : styles.itemTextSmall}
        testID={`searchResultText${item.id}`}
      >
        {bottomLine}
      </TWCText>
    </>
  );
};

/**
 * Returns the correct default home name when adding a new location that doesn't auto-populate the name (zip code, city).
 * Does this by returning names in the order of `Home`, `Home 2`, `Home 3` etc. that are not already names of existing
 * locations for the user.
 * @param {Array} existingLocationNames The names for the existing user locations
 * @returns The next default name in the list of Home, Home 2, Home 3, etc. that is not already in existingLocationNames.
 */
const homeNameFromExisting = (existingLocationNames, homeString) => {
  const namesToCheck = new Set(existingLocationNames.map(name => name.toLowerCase()));
  let currentName = homeString;
  let index = 2;
  let nameFound = namesToCheck.has(currentName.toLowerCase()) || namesToCheck.has(`${homeString} 1`.toLowerCase());

  while (nameFound) {
    currentName = `${homeString} ${index}`;
    nameFound = namesToCheck.has(currentName.toLowerCase()) || namesToCheck.has(`${homeString}${index}`.toLowerCase());
    index++;
  }
  return currentName;
};

export const AddLocationPanel = ({ currentLocationNames, setVisiblePanel, addNewLocation, userSettings }) => {
  const isKeyBoardOpen = useKeyboard();
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const [searchResults, setSearchResults] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setLocationSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const searchTextInputReference = useContextReference(locationsIDs.addNewLocationTextInput);
  const nameTextInputReference = useRef(null);
  const resultsListReference = useRef(null);
  const saveButtonReference = useRef(null);
  const cancelButtonReference = useRef(null);
  const nameInputHandle = useRef(null);
  const shouldFocusNameInput = useRef(false);

  const searchTextInputHandle = useNodeHandle(searchTextInputReference);
  const saveButtonHandle = useNodeHandle(saveButtonReference);
  const cancelButtonHandle = useNodeHandle(cancelButtonReference);
  const nameTextInputHandle = useNodeHandle(nameTextInputReference);
  const resultsListHandle = useNodeHandle(resultsListReference);

  useEffect(() => {
    if (shouldFocusNameInput.current && !isKeyBoardOpen) {
      setTimeout(() => {
        FOCUS_NOW(nameTextInputReference);
      }, 10);
    }
  }, [isKeyBoardOpen]);

  const useTVEventHandler = require('react-native').useTVEventHandler || (() => null);

  const myTVEventHandler = event => {
    if (isAndroid && event.eventType === 'focus') {
      const target = event.target;

      if (target === nameInputHandle.current) {
        shouldFocusNameInput.current = true;
      }
    }
  };

  useTVEventHandler(myTVEventHandler);

  const setSelectedItem = item => {
    if (item) {
      const placeType = item.place_type[0];

      if (placeType === 'place' || placeType === 'postcode') {
        setSelectedItemName(null);
      } else {
        setSelectedItemName(homeNameFromExisting(currentLocationNames, t('newLocationPlaceholder')));
      }
      FOCUS_NOW(searchTextInputReference);
    }
    setLocationSelectedItem(item);
  };

  const { data, isFetching } = useGetLocationsSearch(searchText);

  const coords = selectedItem
    ? { lat: `${selectedItem.center[1]}`, lng: `${selectedItem.center[0]}` }
    : { lat: null, lng: null };

  const { data: selectedItemData, isFetching: selectedItemIsLoading } = useGetLocationFromCoords(coords);

  useEffect(() => {
    if (selectedItemData && !selectedItemData.errors && selectedItem && selectedItemName === null) {
      setSelectedItemName(selectedItemData?.city?.cityStateName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemData, selectedItem]);

  useEffect(() => {
    if (searchText.length > 2 && data && !data.errors) {
      setSearchResults(data);
    } else {
      setSearchResults(null);
    }
  }, [searchText, data]);

  const onSaveLocation = () => {
    // Check if data exists from local now API
    if (selectedItemData) {
      addNewLocation({
        id: selectedItem.id, // ID from mapbox, should be unique for every address
        fullName: selectedItem.place_name, // Usually full address
        coords: {
          lat: selectedItem.center[1], // center array is [lng, lat]
          lng: selectedItem.center[0],
        },
        name: selectedItemName, // User selected name for location
        city: selectedItemData?.city?.name, // City name from local now API
        state: selectedItemData?.city?.stateCode, // State code from local now API
        timeZone: selectedItemData?.city?.timeZone, // Time zone from local now API
        market: selectedItemData?.city?.market,
        isDefault: false,
        isWatching: false,
      });
      const nextLocation = `${selectedItemData?.city?.stateCode?.toLowerCase()}${selectedItemData?.city?.name?.replace(
        /\s/g,
        '',
      )}`;

      segmentTrackEvent(segmentEventTypes.LOCATION_ADDED, userSettings, {
        manageLocations: {
          nextLocation,
        },
      });
      setVisiblePanel({ key: 'list' });
    } else {
      // Show an error message and focus cancel button if there's no data from local now API
      setShowErrorMessage(true);
      FOCUS_NOW(cancelButtonReference);
    }
  };

  const dataIsLoading = selectedItemIsLoading || isFetching;

  const middleFocusNode = dataIsLoading
    ? null
    : selectedItem
    ? nameTextInputHandle
    : searchResults
    ? resultsListHandle
    : null;

  const hideDisplay = { display: 'none' };

  return (
    <View style={styles.panel}>
      <TWCText
        fontFamily={'light'}
        importantForAccessibility={'no'}
        style={styles.titleText}
        testID={locationsIDs.addNewLocationHeadingText}
      >
        {t('headingText')}
      </TWCText>
      <TWCTextInput
        accessibilityHint={t(
          selectedItem
            ? 'searchSelectedItemAccessibilityHint'
            : searchResults
            ? 'searchResultsAccessibilityHint'
            : 'searchAccessibilityHint',
          {
            search: searchText,
          },
        )}
        accessibilityLabel={t('searchAccessibilityLabel')}
        hideKeyboardInput
        keyboardBackground={keyboardBackground}
        keyboardDismissConfig={{ right: true }}
        keyboardWidth={keyboardWidth}
        keyboardWrapConfig={{ horizontal: false }}
        keyboardType={'visible-password'}
        nextFocusDown={middleFocusNode}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={null}
        offsetX={Theme.sidePanelWidth - Theme.appWidth}
        placeholder={t('searchBoxPlaceholderText')}
        ref={searchTextInputReference}
        returnKeyType={'search'}
        style={styles.searchTextInput}
        testID={locationsIDs.addNewLocationTextInput}
        useMiniKeyboard
        value={searchText}
        onChangeText={text => {
          setSelectedItem(null);
          setSearchText(text);
        }}
        onSubmitEditing={() => {
          if (searchText && searchText.length > 0) {
            segmentTrackEvent(segmentEventTypes.LOCATION_SEARCHED, userSettings, {
              manageLocations: {
                searchTerm: searchText,
              },
            });
          }
        }}
      />
      {dataIsLoading ? (
        <View style={styles.loadingIconWrapper}>
          <ActivityIndicator noTestID color={Theme.loading} size={'large'} />
        </View>
      ) : null}

      <View style={!selectedItem && !dataIsLoading ? null : hideDisplay}>
        <FocusableFlatList
          data={searchResults}
          keyExtractor={item => `${item.id}`}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={searchTextInputReference}
          ref={resultsListReference}
          renderItem={(item, index) => (
            <SearchResultsItem
              index={index}
              item={item}
              length={searchResults.length}
              testID={`${locationsIDs.addNewLocationSearchResultRowItem}${index}`}
              onPress={() => {
                setSelectedItem(item);
                const newSearchText = item.place_name.replace(US_TEXT, '');

                setSearchText(newSearchText);
                AccessibilityInfo.announceForAccessibility(
                  t('searchSelectedItemAccessibilityHint', {
                    search: newSearchText,
                  }),
                );
              }}
            />
          )}
          shouldUseContextReference={false}
          style={styles.list}
          wrapItem={false}
        />
      </View>

      <View style={[styles.nameAndButtonWrapper, selectedItem && !dataIsLoading ? null : hideDisplay]}>
        <TWCText noTestID importantForAccessibility={'no'} style={styles.subTitleText}>
          {t(`${showErrorMessage ? 'addLocationFails' : 'textBoxLabel'}`)}
        </TWCText>
        <View style={[showErrorMessage ? hideDisplay : null]}>
          <TWCTextInput
            accessibilityHint={t('nameForLocationHint')}
            accessibilityLabel={t('nameForLocationLabel', {
              name: selectedItemName,
              location: searchText,
            })}
            hideKeyboardInput
            keyboardBackground={keyboardBackground}
            keyboardWidth={keyboardWidth}
            nextFocusDown={showErrorMessage ? cancelButtonHandle : saveButtonHandle}
            nextFocusLeft={null}
            nextFocusRight={null}
            nextFocusUp={searchTextInputHandle}
            offsetX={Theme.sidePanelWidth - Theme.appWidth}
            ref={nameTextInputReference}
            returnKeyType={'done'}
            style={styles.searchTextInput}
            testID={locationsIDs.addNewLocationNameTextInput}
            useMiniKeyboard
            value={selectedItemName}
            onChangeText={text => setSelectedItemName(text)}
            onInputHandle={inputHandle => {
              nameInputHandle.current = inputHandle;
            }}
            onSubmitEditing={() => {
              shouldFocusNameInput.current = false;
            }}
          />
        </View>
        <View style={styles.bottomButtons}>
          <View style={[showErrorMessage ? hideDisplay : null]}>
            <TWCButton
              accessibilityHint={t('saveButtonAccessibilityHint', {
                location: searchText,
                name: selectedItemName,
              })}
              accessibilityLabel={t('saveButtonAccessibilityLabel')}
              nextFocusDown={null}
              nextFocusLeft={null}
              nextFocusRight={cancelButtonHandle}
              nextFocusUp={middleFocusNode ?? searchTextInputHandle}
              ref={saveButtonReference}
              shouldUseContextReference={false}
              style={styles.button}
              testID={locationsIDs.addNewLocationSaveButton}
              title={t('saveButtonText')}
              onPress={onSaveLocation}
            />
          </View>
          <TWCButton
            accessibilityHint={t('cancelButtonAccessibilityHint')}
            accessibilityLabel={t('cancelButtonAccessibilityLabel')}
            nextFocusDown={null}
            nextFocusLeft={showErrorMessage ? null : saveButtonHandle}
            nextFocusRight={null}
            nextFocusUp={middleFocusNode ?? searchTextInputHandle}
            ref={cancelButtonReference}
            shouldUseContextReference={false}
            style={styles.button}
            testID={locationsIDs.addNewLocationCancelButton}
            title={t('cancelButtonText')}
            onPress={() => {
              setVisiblePanel({ key: 'list' });
              if (showErrorMessage) {
                setShowErrorMessage(false);
              }
            }}
          />
        </View>
      </View>
    </View>
  );
};

AddLocationPanel.propTypes = {
  currentLocationNames: PropTypes.array.isRequired,
  setVisiblePanel: PropTypes.func,
  addNewLocation: PropTypes.func,
  userSettings: PropTypes.object,
};

AddLocationPanel.defaultProps = {
  setVisiblePanel: () => {},
  addNewLocation: () => {},
  userSettings: null,
};

const styles = StyleSheet.create({
  bottomButtons: {
    bottom: Theme.px(100),
    flex: 1,
    flexDirection: 'row',
    left: Theme.px(-26),
    position: 'absolute',
  },
  button: { marginRight: Theme.px(25) },
  itemText: {
    color: Theme.white,
    fontSize: Theme.px(38),
    marginLeft: isWeb ? Theme.px(25) : Theme.px(21),
    marginTop: isWeb ? Theme.px(12) : Theme.px(0),
  },
  itemTextFocused: {
    color: Theme.colorFocusText,
  },
  itemTextSmall: {
    color: Theme.white,
    fontSize: Theme.px(27),
    marginLeft: isWeb ? Theme.px(25) : Theme.px(21),
    marginTop: Theme.px(5),
  },
  itemTextWrapper: {
    borderRadius: Theme.tileBorderRadius,
    height: Theme.px(100),
    marginBottom: Theme.px(10),
    top: Theme.px(4),
    width: Theme.px(800),
  },
  itemTextWrapperFocused: {
    backgroundColor: Theme.white,
  },
  list: {
    marginLeft: Theme.px(-26),
    marginTop: Theme.px(10),
  },
  listWrapper: {
    height: Theme.px(620),
    position: 'absolute',
    width: '100%',
  },
  listsWrapper: {
    height: '100%',
    marginTop: Theme.px(0),
    position: 'relative',
    width: '100%',
  },
  loadingIconWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginLeft: Theme.px(-26),
    marginTop: Theme.px(50),
    width: Theme.px(800),
  },
  loadingIndicator: {
    height: '100%',
    marginLeft: Theme.px(-76),
  },
  nameAndButtonWrapper: {
    height: Theme.px(740),
  },
  panel: {
    height: '100%',
  },
  searchTextInput: {
    marginLeft: Theme.px(-26),
  },
  subTitleText: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(30),
    marginBottom: Theme.px(15),
    marginTop: Theme.px(60),
  },
  titleText: {
    color: Theme.white,
    fontSize: Theme.px(60),
    marginBottom: Theme.px(60),
    marginTop: Theme.px(80),
  },
});
